import { Address, IPersonalAddressState } from '@interfaces';
import { UploadedFileEntry } from '@interfaces/mfe/mfe-document-upload.interface';
import { InvestmentObjectives } from '@interfaces/models/investment-objectives-types';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { MFEAddress } from '@interfaces/models/open-account-mfes/mfe-common.interfaces';
import { PersonalAddressData } from '@interfaces/models/open-account-mfes/personal-address-mfe.model';
import { IReceipt, ReceiptRow } from '@interfaces/models/receipt.interface';
import { CitizenshipStatusEnum, SuffixValuesEnum } from '@interfaces/open-account/enums';
import { FlowTypes, Move_To_New, TransactionTypesEnum, urlMapper } from '@shared/models';
import { ClearData } from '@shared/models/prospect-data-models.interface';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import { FinancialDetailsState } from '@store/reducers/income-net-worth/income-net-worth.reducer';
import { ILetterUploadState } from '@store/reducers/letter-upload/letter-upload.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { MfePersonalAddressState } from '@store/reducers/personal-info-address/personal-info-address.reducers';
import {
  getIsClient,
  getIsExecutor,
  getIsInternalTransferFlow,
  getIsIRATrust,
  getIsOnlyTrustee,
  getIsSingleExecutor,
  getIsTrustToTrustFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { IPersonalPhoneState } from 'vg-interaction-model/dist/lib/features/personal-phone/store/reducers/personal-phone.reducer';

import { joinWithSpace } from '../cashier-nonretirement';
import { buildAccountApprovalLetterRow } from '../receipt.helpers';

interface Profile {
  header: string;
  description?: string | null;
  editable: boolean;
  editRoute: string;
  sectionEvidenceID: string;
  rows: ReceiptRow[];
}

const config = getConfigClientFromRTE();

export function mapGenderToReceiptValue(gender: string): string {
  if (!gender) {
    return 'Unknown';
  }

  switch (gender.toLowerCase()) {
    case 'male':
    case 'm':
      return 'Male';
    case 'female':
    case 'f':
      return 'Female';
    case 'undisclosed':
    case 'u':
      return 'Undisclosed';
    default:
      return 'Unknown';
  }
}

export function isSameAddress(firstAddress: Address, secondAddress: Address): boolean {
  if (!firstAddress || !secondAddress) {
    return false;
  }

  const firstRecord = firstAddress as unknown as Record<string, unknown>;
  const secondRecord = secondAddress as unknown as Record<string, unknown>;
  const firstAddressKeys = Object.keys(firstRecord);
  const secondAddressKeys = Object.keys(secondRecord);

  if (firstAddressKeys.length !== secondAddressKeys.length) {
    return false;
  }

  for (const key of firstAddressKeys) {
    if (!(key in secondRecord)) {
      return false;
    }
    const firstVal = firstRecord[key];
    const secondVal = secondRecord[key];
    if (Array.isArray(firstVal) && Array.isArray(secondVal)) {
      if (firstVal.length !== secondVal.length) {
        return false;
      }
      for (let i = 0; i < firstVal.length; i++) {
        if (firstVal[i] !== secondVal[i]) {
          return false;
        }
      }
    } else if (!Array.isArray(firstVal) && !Array.isArray(secondVal)) {
      if (firstVal !== secondVal) {
        return false;
      }
    } else {
      return false;
    }
  }
  return true;
}

export function safeAttributeAccessor(value: string): string {
  return value ? value : '';
}

export function splitClientId(id: string) {
  return id.slice(id.length - 4);
}

export function profileDescriptionLink() {
  const link = config.pretty_ecs_domain + config.account_setting_endpoint;
  return ReviewContent.profileDescription.replace('{{link}}', link);
}
export function safeRowBuilder(
  rows: ReceiptRow[],
  label: string,
  value: string,
  evidenceID: string,
) {
  if (value) {
    rows.push({
      label: label,
      values: [value],
      evidenceID: evidenceID,
    });
  }
}

export function safeNameBuilder(
  rows: ReceiptRow[],
  label: string,
  personalInfo: MfePersonalInfoClientState['personalInfo'],
  evidenceID: string,
) {
  if (personalInfo.firstName) {
    const name = joinWithSpace([
      personalInfo.firstName,
      personalInfo.middleName,
      personalInfo.lastName,
      SuffixValuesEnum[personalInfo.suffix as keyof typeof SuffixValuesEnum],
    ]);
    rows.push({
      label: label,
      values: [name],
      evidenceID: evidenceID,
    });
  }
}

// eslint-disable-next-line max-params, max-statements
export function buildProfileReceipt(
  triageState: TriageState,
  transactionType: TransactionTypesEnum | null,
  personalInfo: MfePersonalInfoClientState['personalInfo'],
  ssn_private: string,
  address: MfePersonalAddressState,
  phone: IPersonalPhoneState,
  clearData: ClearData,
  isNewAccountTransaction: boolean,
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.profile,
  transactionWithExecutorInfo?: boolean,
  transactionWithTrusteeInfo?: boolean,
): IReceipt {
  const flow = urlMapper[triageState.flowType];
  const isExecutor = getIsExecutor(triageState);
  const isSingleExecutor = getIsSingleExecutor(triageState);
  const isSingleTrustee = getIsOnlyTrustee(triageState);
  const isTrustToTrustFlow = getIsTrustToTrustFlow(triageState);
  const isMultiInheritanceFlow = triageState.flowType === FlowTypes.MULTI_INHERITANCE;
  const client: boolean = getIsClient(triageState);
  let header = '';

  if (
    isMultiInheritanceFlow &&
    !isExecutor &&
    !isSingleTrustee &&
    !transactionWithExecutorInfo &&
    !transactionWithTrusteeInfo
  ) {
    header = 'Profile';
  } else if (isExecutor || transactionWithExecutorInfo) {
    header = 'Executor information';
  } else if (isSingleTrustee || transactionWithTrusteeInfo) {
    header = 'Trustee Information';
  } else if (isTrustToTrustFlow) {
    header = 'Personal Information';
  } else {
    header = content?.profileLabel || 'Profile';
  }

  const profile: Profile = {
    header: header,
    editable: editable && client,
    editRoute: `secure/${flow}/personal-info`,
    sectionEvidenceID: 'profile',
    rows: [] as ReceiptRow[],
  };

  if ((transactionType && Move_To_New.has(transactionType)) || isMultiInheritanceFlow) {
    buildNewAccountProfile(
      profile,
      personalInfo,
      ssn_private,
      address,
      phone,
      editable,
      client,
      isTrustToTrustFlow || false,
      isSingleExecutor,
      triageState.flowType,
      isNewAccountTransaction,
      content,
    );
  } else {
    profile.editable = false;
    safeNameBuilder(profile.rows, content?.nameLabel || 'Name', personalInfo, 'profileName');
  }

  if (triageState.isSpouse && !isMultiInheritanceFlow) {
    profile.rows.push({
      label: 'Relationship',
      values: [triageState.isSpouse === 'YES' ? 'Spouse' : 'Nonspouse'],
      evidenceID: 'profileRelationship',
    });
  }

  if (clearData?.uploadedFilename) {
    profile.rows.push({
      label: 'Document for verification',
      values: [clearData.uploadedFilename],
      evidenceID: 'clearDocumentVerification',
    });
  }

  return profile;
}

export function buildNewAccountProfile(
  profile: Profile,
  personalInfo: MfePersonalInfoClientState['personalInfo'],
  ssn_private: string,
  address: MfePersonalAddressState,
  phone: IPersonalPhoneState,
  editable: boolean,
  client: boolean,
  isTrustToTrustFlow: boolean,
  isSingleExecutor: boolean,
  flow: FlowTypes,
  isNewAccountTransaction: boolean,
  content?: typeof ReviewContent.receiptContent.profile,
) {
  const isMultiInheritance = flow === FlowTypes.MULTI_INHERITANCE;
  const isExistingAccountMultiInheritance = isMultiInheritance && !isNewAccountTransaction;
  profile.description =
    (editable && client && !isTrustToTrustFlow) || isSingleExecutor
      ? profileDescriptionLink()
      : null;

  if (isMultiInheritance) {
    profile.description = null;
  }

  safeNameBuilder(profile.rows, content?.nameLabel || 'Name', personalInfo, 'profileName');
  safeRowBuilder(
    profile.rows,
    content?.emailLabel || 'Email',
    safeAttributeAccessor(personalInfo.email),
    'profileEmail',
  );
  safeRowBuilder(
    profile.rows,
    content?.dateOfBirthLabel || 'Date of birth',
    safeAttributeAccessor(personalInfo.birthDate),
    'profileDob',
  );

  if (personalInfo.citizenshipStatus) {
    let citizenship = 'Non-resident alien';
    switch (personalInfo.citizenshipStatus) {
      case CitizenshipStatusEnum.UsCitizen:
        citizenship = 'U.S. citizen';
        break;
      case CitizenshipStatusEnum.ResidentAlien:
        citizenship = 'Resident alien';
        break;
    }
    safeRowBuilder(
      profile.rows,
      content?.citizenshipLabel || 'Citizenship',
      safeAttributeAccessor(citizenship),
      'profileCitizenship',
    );

    safeRowBuilder(
      profile.rows,
      content?.countryOfCitizenshipLabel || 'Country of citizenship',
      personalInfo.countryOfCitizenship ? personalInfo.countryOfCitizenship : 'US',
      'profileCountryOfCitizenship',
    );
  }

  if (editable) {
    safeRowBuilder(
      profile.rows,
      content?.socialSecurityNoLabel || 'Social Security number',
      ssn_private,
      'profileSsn-private',
    );
  } else {
    safeRowBuilder(
      profile.rows,
      content?.socialSecurityNoLabel || 'Social Security number',
      '***-**-' + splitClientId(ssn_private),
      'profileSsn-private',
    );
  }

  if (isMultiInheritance) {
    safeRowBuilder(
      profile.rows,
      'Gender',
      mapGenderToReceiptValue(personalInfo.gender),
      'profileGender',
    );
  }

  const primaryResidenceLabel = isTrustToTrustFlow ? 'Address' : 'Primary residence';

  if (!isExistingAccountMultiInheritance) {
    buildReceiptAddress(
      profile.rows,
      primaryResidenceLabel,
      address.personalAddress.streetAddress,
      'profilePrimaryResidenceAddress',
    );
  }

  if (!isTrustToTrustFlow && !isExistingAccountMultiInheritance) {
    buildReceiptAddress(
      profile.rows,
      'Mailing address',
      address.personalAddress.mailingAddress,
      'profileMailingAddr',
      isMultiInheritance &&
        isSameAddress(
          address.personalAddress.streetAddress,
          address.personalAddress.mailingAddress,
        ),
    );
  }

  if (!isExistingAccountMultiInheritance) {
    const primaryPhoneLabel = isTrustToTrustFlow ? 'Phone' : 'Primary phone';
    buildReceiptPhone(profile.rows, primaryPhoneLabel, phone, 'profilePrimaryPhone');
  }
}

export function buildReceiptAddress(
  rows: ReceiptRow[],
  label: string,
  addrType: Address,
  evidenceID: string,
  isSameAddress: boolean = false,
) {
  if (addrType && addrType.street) {
    rows.push({
      label: label,
      values: isSameAddress
        ? ['Same as primary residence']
        : [addrType.street, addrType.city + ', ' + addrType.state + ' ' + addrType.zipCode],
      evidenceID: evidenceID,
    });
  }
}

export function buildReceiptPhone(
  rows: ReceiptRow[],
  label: string,
  phone: IPersonalPhoneState,
  evidenceID: string,
) {
  if (phone?.phoneNumber) {
    const isMobile = phone.isMobile ? ' (Cell phone)' : '';
    rows.push({
      label: label,
      values: [phone.phoneNumber + isMobile],
      evidenceID: evidenceID,
    });
  }
}

export function buildEmploymentReceipt(
  state: Partial<VgEmploymentState> | undefined,
  transactionType: TransactionTypesEnum | null,
  internalTransferTransactionType: TransactionTypesEnum | null,
  triageState: TriageState,
  letterUploadState: ILetterUploadState,
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.employment,
  wldLetterDocuments: UploadedFileEntry[] = [],
): IReceipt | null {
  if (
    transactionType === TransactionTypesEnum.EXISTING ||
    internalTransferTransactionType === TransactionTypesEnum.EXISTING
  ) {
    return null;
  }

  const empStatus = state?.employmentStatus;
  const empAssociations = state?.associations;
  const {
    street = '',
    city = '',
    state: addressState = '',
    zipCode = '',
  } = empStatus?.streetAddress || {};

  const statusCode = empStatus?.employmentStatus || '';
  let statusCodeText: string;
  const businessAddressLine1 = street;
  const businessAddressLine2 = [city, [addressState, zipCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const employer = empStatus?.employer || '';
  const occupation = empStatus?.occupation || '';
  const employmentStatusRows = [
    {
      label: content?.employerLabel || 'Employer',
      values: [empStatus?.employmentStatus === 'VE' ? 'Vanguard' : employer],
      evidenceID: 'employmentEmployer',
    },
    {
      label: content?.occupationLabel || 'Occupation',
      values: [occupation],
      evidenceID: 'employmentOccupation',
    },
    {
      label: content?.businessStreetAddressLabel || 'Business street address',
      values: [businessAddressLine1, businessAddressLine2],
      evidenceID: 'employmentAddress',
    },
  ];

  const selfEmployedRows = [
    {
      label: content?.companyNameLabel || 'Company name',
      values: [employer],
      evidenceID: 'employmentEmployer',
    },
    {
      label: content?.natureOfBusinessLabel || 'Nature of business',
      values: [occupation],
      evidenceID: 'employmentOccupation',
    },
  ];

  const associationsRows = [
    {
      label: content?.spouseIsVgEmployeeLabel || 'Spouse is a Vanguard employee',
      values: [empAssociations?.spouse ? 'Yes' : 'No'],
      evidenceID: 'employmentSpouseIsVanguardEmployee',
    },
    {
      label: content?.associatedPersonLabel || 'Associated person',
      values: [empAssociations?.associated ? 'Yes' : 'No'],
      evidenceID: 'employmentAssociatedPerson',
    },
  ];

  const approvalLetterRow = buildAccountApprovalLetterRow(letterUploadState, wldLetterDocuments);
  if (approvalLetterRow) {
    associationsRows.push(approvalLetterRow);
  }

  associationsRows.push({
    label: content?.controlPersonLabel || 'Control person',
    values: [empAssociations?.control ? 'Yes' : 'No'],
    evidenceID: 'employmentControlPerson',
  });

  let controlCompaniesRows: ReceiptRow[] = [];
  if (empAssociations?.control) {
    const companies = [];
    const tickers = empAssociations.controlCompanyTicker ?? [];
    const names = empAssociations.controlCompanyName ?? [];
    const length = Math.min(tickers.length, names.length);
    for (let ind = 0; ind < length; ind++) {
      companies.push(`${tickers[ind]}-${names[ind]}`);
    }
    controlCompaniesRows = [
      {
        label: 'Control companies',
        values: [...companies],
        evidenceID: 'controlPersonCompanies',
      },
    ];
  }

  const flow = urlMapper[triageState.flowType];

  const receipt: IReceipt = {
    header: content?.employmentLabel || 'Employment',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/employment`,
    sectionEvidenceID: 'employment',
    rows: [
      {
        label: 'Status',
        values: [],
        evidenceID: 'employmentStatus',
      },
    ],
  };

  switch (statusCode) {
    case 'E':
      statusCodeText = 'Employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...employmentStatusRows, ...associationsRows, ...controlCompaniesRows);
      break;
    case 'VE':
      statusCodeText = 'Employed by Vanguard';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(employmentStatusRows[1], ...associationsRows, ...controlCompaniesRows);
      break;
    case 'SE':
      statusCodeText = 'Self-employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(
        ...selfEmployedRows,
        employmentStatusRows[2],
        ...associationsRows,
        ...controlCompaniesRows,
      );
      break;
    case 'R':
      statusCodeText = 'Retired';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...associationsRows, ...controlCompaniesRows);
      break;
    case 'U':
      statusCodeText = 'Not employed';
      receipt.rows[0].values = [statusCodeText];
      receipt.rows.push(...associationsRows, ...controlCompaniesRows);
      break;
    default:
      break;
  }
  return receipt;
}

const objectiveLabels: Readonly<Record<keyof InvestmentObjectives, string>> = {
  CAP: 'Capital preservation',
  Income: 'Income',
  Growth: 'Growth',
  Speculation: 'Speculation',
};

export function buildInvestmentObjectivesDescription(invObj: {
  objectives: InvestmentObjectives;
}): string {
  const keys: readonly (keyof InvestmentObjectives)[] = ['CAP', 'Income', 'Growth', 'Speculation'];
  const selected = keys.filter((key) => invObj.objectives[key]).map((key) => objectiveLabels[key]);
  return selected.length ? selected.join('<br>') + '<br>' : '';
}

export function buildFinancialProfileReceipt(
  financial: FinancialDetailsState,
  invObj: { objectives: InvestmentObjectives },
  editable: boolean,
  transactionType: TransactionTypesEnum | null,
  internalTransferTransactionType: TransactionTypesEnum | null,
  triageState: TriageState,
  content?: typeof ReviewContent.receiptContent.financialProfile,
  isItMultiReceivingToggle: boolean = false,
) {
  if (
    transactionType === TransactionTypesEnum.EXISTING ||
    internalTransferTransactionType === TransactionTypesEnum.EXISTING
  ) {
    return null;
  }
  const flow = urlMapper[triageState.flowType];

  const moneyOptions: Record<string, string> = {
    G: 'Less than $20,000',
    H: '$20,000 - $49,999',
    I: '$50,000 - $74,999',
    J: '$75,000 - $99,999',
    K: '$100,000 - $499,999',
    L: '$500,000 - $999,999',
    M: '$1,000,000 or above',
  };
  const objectives: string = buildInvestmentObjectivesDescription(invObj);
  const isExecutor = getIsExecutor(triageState);
  const isIRATrustee = getIsIRATrust(triageState);
  const isTrustToTrustFlow = getIsTrustToTrustFlow(triageState);

  const financialProfileReceipt: IReceipt = {
    header: content?.financialProfileLabel || 'Financial profile',
    description: null,
    editable: editable,
    editRoute: `secure/${flow}/${isExecutor || isIRATrustee || isTrustToTrustFlow ? 'investment-objectives' : 'income-net-worth'}`,
    sectionEvidenceID: 'financialProfile',
    rows: [
      {
        label: content?.investmentObjectiveLabel || 'Investment objective',
        values: [objectives],
        evidenceID: 'investmentObjectives',
      },
    ],
  };

  // Investment objectives shouldn't be on global financial profile section in multi IT
  if (getIsInternalTransferFlow(triageState) && isItMultiReceivingToggle) {
    financialProfileReceipt.rows = [];
  }

  if (financial.annualIncome) {
    financialProfileReceipt.rows.push(
      {
        label: content?.incomeLabel || 'Income',
        values: [moneyOptions[financial.annualIncome]],
        evidenceID: 'income',
      },
      {
        label: content?.netWorthLabel || 'Net worth',
        values: [moneyOptions[financial.liquidNetWorth]],
        evidenceID: 'networth',
      },
    );
  }
  return financialProfileReceipt;
}
